import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getVendorById, getVendorAssessments } from 'services/vendor-service';
import { VendorForm } from './VendorForm';
import { formatDateToDateBR } from 'utils/date_utils';

/**
 * VendorDetail Component
 * Displays comprehensive vendor information including assessment history
 */
export const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['vendor', 'common']);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Fetch vendor data
  const { data: vendorData, isLoading: vendorLoading } = useQuery(
    ['vendor', id],
    () => getVendorById(id),
    {
      enabled: !!id
    }
  );

  // Fetch assessment history
  const { data: assessmentsData, isLoading: assessmentsLoading } = useQuery(
    ['vendor-assessments', id],
    () => getVendorAssessments(id),
    {
      enabled: !!id
    }
  );

  // Handle API response with success field
  const vendor = vendorData?.data?.success ? vendorData.data.data : null;
  const assessments = assessmentsData?.data?.data || [];

  const getTierBadge = (tier) => {
    const key = String(tier);
    const validKeys = ['1', '2', '3'];
    if (validKeys.includes(key)) {
      return {
        label: t(`vendor:tier.${key}.fullLabel`),
        color: key === '1' ? 'error' : key === '2' ? 'warning' : 'success',
        description: t(`vendor:tier.${key}.description`)
      };
    }
    return {
      label: t('vendor:tier.unknown.fullLabel'),
      color: 'default',
      description: t('vendor:tier.unknown.description')
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon />;
      case 'Rejected':
        return <CancelIcon />;
      default:
        return <AssessmentIcon />;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Under Review':
        return 'warning';
      case 'Pending Vendor Response':
        return 'default';
      case 'Draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTrustScoreColor = (score) => {
    if (!score || typeof score !== 'number') return 'default';
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  // TimelineDot requires different color values than Chip
  // Valid colors: 'grey', 'inherit', 'primary', 'secondary', 'error', 'info', 'success', 'warning'
  const getTimelineDotColor = (status) => {
    if (!status) return 'grey';
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Under Review':
        return 'warning';
      case 'Pending Vendor Response':
        return 'primary';
      case 'Draft':
        return 'grey';
      default:
        return 'grey';
    }
  };

  if (vendorLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!vendor) {
    return (
      <Box p={4}>
        <Typography color="error">{t('vendor:detail.notFound')}</Typography>
      </Box>
    );
  }

  const tierBadge = getTierBadge(vendor.tier);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/vendors')}
          >
            {t('vendor:detail.backToVendors')}
          </Button>
          <Typography variant="h4">{vendor.name}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setIsEditDialogOpen(true)}
        >
          {t('vendor:form.title.edit')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Vendor Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('vendor:detail.sections.info')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {t('vendor:detail.fields.contactEmail')}
                  </Typography>
                  <Typography variant="body1">{vendor.contactEmail}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {t('vendor:detail.fields.contactName')}
                  </Typography>
                  <Typography variant="body1">
                    {vendor.contactName || t('vendor:detail.notSpecified')}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {t('vendor:detail.fields.relationshipDescription')}
                  </Typography>
                  <Typography variant="body1">
                    {vendor.relationshipDescription || t('vendor:detail.noDescription')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {t('vendor:detail.fields.createdDate')}
                  </Typography>
                  <Typography variant="body1">
                    {formatDateToDateBR(vendor.createdAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {t('vendor:detail.fields.lastUpdated')}
                  </Typography>
                  <Typography variant="body1">
                    {formatDateToDateBR(vendor.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Classification */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('vendor:detail.sections.risk')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  {t('vendor:detail.fields.riskTier')}
                </Typography>
                <Box mt={1}>
                  <Chip
                    label={tierBadge.label}
                    color={tierBadge.color}
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Typography variant="caption" display="block" mt={1} color="textSecondary">
                    {tierBadge.description}
                  </Typography>
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  {t('vendor:detail.fields.dataSensitivity')}
                </Typography>
                <Typography variant="h6">{vendor.dataSensitivity}/5</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  {t('vendor:detail.fields.businessCriticality')}
                </Typography>
                <Typography variant="h6">{vendor.businessCriticality}/5</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  {t('vendor:detail.fields.inherentRiskScore')}
                </Typography>
                <Typography variant="h6">{vendor.inherentRiskScore}/5</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t('vendor:detail.fields.dpaStatus')}
                </Typography>
                <Box mt={1}>
                  <Chip
                    icon={vendor.dpaEstablished ? <CheckCircleIcon /> : <CancelIcon />}
                    label={vendor.dpaEstablished ? t('vendor:detail.dpa.established') : t('vendor:detail.dpa.none')}
                    color={vendor.dpaEstablished ? 'success' : 'default'}
                    variant={vendor.dpaEstablished ? 'filled' : 'outlined'}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t('vendor:detail.fields.recertificationPeriod')}
                </Typography>
                <Typography variant="body1">
                  {vendor.effectiveRecertificationPeriodMonths
                    ? t('vendor:detail.recertification.months', { count: vendor.effectiveRecertificationPeriodMonths })
                    : t('vendor:detail.recertification.tierDefault')}
                  {' '}
                  {vendor.recertificationPeriodMonths
                    ? t('vendor:detail.recertification.customOverride')
                    : t('vendor:detail.recertification.tierDefaultLabel')}
                </Typography>
              </Box>

              {vendor.recertificationDueDate && (
                <Box mt={1}>
                  <Typography variant="caption" color="textSecondary">
                    {t('vendor:detail.fields.reassessmentDue')}
                  </Typography>
                  <Box mt={0.5}>
                    {vendor.recertificationDue ? (
                      <Chip
                        label={t('vendor:detail.recertification.due', { date: formatDateToDateBR(vendor.recertificationDueDate) })}
                        color="error"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label={formatDateToDateBR(vendor.recertificationDueDate)}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Assessment History Timeline */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('vendor:detail.sections.assessmentHistory')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {assessmentsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : assessments.length === 0 ? (
                <Typography color="textSecondary" align="center" p={3}>
                  {t('vendor:detail.noAssessments')}
                </Typography>
              ) : (
                <Timeline>
                  {assessments.map((assessment, index) => (
                    <TimelineItem key={assessment._id}>
                      <TimelineOppositeContent color="text.secondary">
                        {formatDateToDateBR(assessment.createdAt)}
                      </TimelineOppositeContent>
                      <TimelineSeparator>
                        <TimelineDot color={getTimelineDotColor(assessment.status)}>
                          {getStatusIcon(assessment.status)}
                        </TimelineDot>
                        {index < assessments.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={1} sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle2">
                                {assessment.frameworkName}
                              </Typography>
                              <Chip
                                label={assessment.status}
                                color={getStatusColor(assessment.status)}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                            {assessment.trustScore && (
                              <Chip
                                label={`${assessment.trustScore}%`}
                                color={getTrustScoreColor(assessment.trustScore)}
                                size="small"
                              />
                            )}
                          </Box>
                          <Button
                            size="small"
                            onClick={() => navigate(`/vendor-assessments/${assessment._id}`)}
                            sx={{ mt: 1 }}
                          >
                            {t('vendor:detail.viewDetails')}
                          </Button>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Vendor Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('vendor:form.title.edit')}</Typography>
            <IconButton onClick={() => setIsEditDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <VendorForm
            vendor={vendor}
            onSuccess={() => {
              setIsEditDialogOpen(false);
              queryClient.invalidateQueries(['vendor', id]);
            }}
            onCancel={() => setIsEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
